import { useTranslation } from 'react-i18next';
import { Divider, Space, Tag, Tooltip, Typography } from 'antd';
import sumBy from 'lodash/sumBy';
import compact from 'lodash/compact';
import flatten from 'lodash/flatten';
import { getDataDetail } from 'utils/tools';
import { DynamicObjectType } from 'types/commons';
import TotalOrderItem from '../components/TotalOrderItem';
import { formatMoney } from 'utils/formattedString';
// import { PAYMENT_TYPES } from 'constants/constants';

const PaymentInformation = ({ data }) => {
  const { t } = useTranslation();

  const promotionInfos = compact([
    ...(compact<DynamicObjectType>(
      flatten(
        data?.items?.map((item: DynamicObjectType) => item?.promotionInfos),
      ),
    )?.map((promotionOnProduct, index) => {
      const promotionInfo = data?.promotionInfos?.find(
        (e) => e.id === promotionOnProduct?.promotionId,
      );
      return {
        id: `product-${promotionOnProduct?.promotionId}-${index}`,
        title: `${promotionInfo?.code ? `${promotionInfo?.code} - ` : ''}${
          getDataDetail(promotionInfo)?.name
        }`,
        value:
          Number(promotionOnProduct?.priceBeforePromotion) -
          Number(promotionOnProduct?.priceAfterPromotion),
        promotionType: promotionInfo?.promotionType,
      };
    }) || []),
    ...(compact<DynamicObjectType>(data?.promotionDeliveryInfos)?.map(
      (promotionOnProduct, index) => {
        const promotionInfo = data?.promotionInfos?.find(
          (e) => e.id === promotionOnProduct?.promotionId,
        );
        return {
          id: `delivery-${promotionOnProduct?.promotionId}-${index}`,
          title: `${promotionInfo?.code ? `${promotionInfo?.code} - ` : ''}${
            getDataDetail(promotionInfo)?.name
          }`,
          value:
            Number(promotionOnProduct?.shippingFeeBeforePromotion) -
            Number(promotionOnProduct?.shippingFeeAfterPromotion),
          promotionType: promotionInfo?.promotionType,
        };
      },
    ) || []),
    ...(compact<DynamicObjectType>(data?.promotionOrderInfos)?.map(
      (promotionOnProduct, index) => {
        const promotionInfo = data?.promotionInfos?.find(
          (e) => e.id === promotionOnProduct?.promotionId,
        );
        return {
          id: `order-${promotionOnProduct?.promotionId}-${index}`,
          title: `${promotionInfo?.code ? `${promotionInfo?.code} - ` : ''}${
            getDataDetail(promotionInfo)?.name
          }`,
          value:
            Number(promotionOnProduct?.totalBeforePromotion) -
            Number(promotionOnProduct?.totalAfterPromotion),
          promotionType: promotionInfo?.promotionType,
        };
      },
    ) || []),
  ]);

  return (
    <Space size={10} className="w-full" direction="vertical">
      <TotalOrderItem
        title={t('orderDetail.totalProducts')}
        value={sumBy(data?.items, 'quantity')}
      />
      <TotalOrderItem
        title={t('orderDetail.subTotal')}
        value={formatMoney(data?.subTotal)}
        boldTitle
        boldValue
      />
      {data?.newOrderExchangeId && (
        <>
          <TotalOrderItem
            title={t('orderDetail.returnDeduction')}
            value={formatMoney(data?.newOrderExchange?.returnItemsValue)}
          />
          <TotalOrderItem
            title={t('orderDetail.shipping')}
            value={formatMoney(data?.newOrderExchange?.shippingFee)}
          />
          <TotalOrderItem
            title={t('orderDetail.totalPrice')}
            value={formatMoney(data?.total)}
            boldTitle
            boldValue
          />
        </>
      )}
      {!data?.newOrderExchangeId && (
        <>
          <TotalOrderItem
            title={t('orderDetail.discountOnOrder')}
            value={formatMoney(data?.discount)}
            isNegative={+data?.discount > 0}
          />
          {promotionInfos
            ?.filter((p: DynamicObjectType) => p?.promotionType === 'order')
            ?.map((promotion) => (
              <Tooltip title={promotion?.title}>
                <Tag
                  key={promotion?.id}
                  className="bg-[--bg-light] border border-[--primary] p-1 rounded-md"
                >
                  <Typography.Paragraph
                    ellipsis={{ rows: 1 }}
                    style={{
                      wordWrap: 'break-word',
                      wordBreak: 'break-word',
                      marginBottom: 0,
                    }}
                    className="max-w-[150px] text-[--primary] text-xs"
                  >
                    {promotion?.title}
                  </Typography.Paragraph>
                </Tag>
              </Tooltip>
            ))}

          {(data?.giftCards?.length > 0 || data?.totalAmountGiftCard > 0) && (
            <>
              <TotalOrderItem
                title={t('orderDetail.giftCards')}
                value={formatMoney(
                  sumBy(data?.giftCards, (e: DynamicObjectType) => +e.amount) ||
                    data?.totalAmountGiftCard,
                )}
                isNegative
              />
              <div>
                {data?.giftCards?.map((giftCard) => (
                  <Tag
                    key={`${giftCard?.giftType}-${giftCard?.code}`}
                    className="bg-[--bg-light] border border-[--primary] p-1 rounded-md text-xs text-[--primary]"
                  >
                    {`${giftCard?.giftType || giftCard?.partnerCode}: ${
                      giftCard?.code
                    }`}
                  </Tag>
                ))}
              </div>
            </>
          )}

          {data?.totalDiscountMarketPlace && (
            <TotalOrderItem
              title={t('orderDetail.discountPriceMarket')}
              value={formatMoney(data?.totalDiscountMarketPlace)}
              isNegative
            />
          )}
          <TotalOrderItem
            title={t('orderDetail.shipping')}
            value={formatMoney(data?.shippingFee)}
          />
          <TotalOrderItem
            className="fw-bold"
            title={t('orderDetail.totalPrice')}
            value={formatMoney(data?.total)}
            boldTitle
            boldValue
          />
          {!data?.isMarketPlace && (
            <TotalOrderItem
              title={t('orders.loyaltyPoint')}
              value={data?.extraData?.estimatedReceivePoint}
            />
          )}
          <Divider className="m-0" />
          <TotalOrderItem
            title={t('orderDetail.reward')}
            value={data?.rewardAmount ? formatMoney(data?.rewardAmount) : ''}
          />
          <TotalOrderItem title={t('orderDetail.directDiscount')} />
          <Space size={5} wrap>
            {data?.promotionInfos
              ?.filter((p: DynamicObjectType) => p?.promotionType !== 'order')
              ?.map((promotion) => (
                <Tooltip
                  title={getDataDetail(promotion, 'detail', 'name', null)?.name}
                >
                  <Tag
                    key={promotion?.id}
                    className="bg-[--bg-light] border border-[--primary] p-1 rounded-md"
                  >
                    <Typography.Paragraph
                      ellipsis={{ rows: 1 }}
                      style={{
                        wordWrap: 'break-word',
                        wordBreak: 'break-word',
                        marginBottom: 0,
                      }}
                      className="max-w-[150px] text-[--primary] text-xs"
                    >
                      {getDataDetail(promotion, 'detail', 'name', null)?.name}
                    </Typography.Paragraph>
                  </Tag>
                </Tooltip>
              ))}
          </Space>
        </>
      )}
    </Space>
  );
};

export default PaymentInformation;
